import { Schema } from "effect";

export class DatabaseError extends Schema.TaggedError<DatabaseError>()(
  "DatabaseError",
  {
    code: Schema.optional(Schema.Number),
    message: Schema.optional(Schema.String),
  },
) {}

/**
 * Walks the `cause` chain of an error value and builds a human-readable string
 * that includes every layer's tag and message.
 *
 * The Effect/@effect/sql error stack typically looks like:
 *   DatabaseError → SqlError → pg Error (the one with the actual details)
 *
 * Calling `String(error)` only returns the top-level message which is almost
 * always useless ("SqlError: Failed to execute QueryPromise").  This helper
 * walks the chain so the DB error surfaces the underlying pg failure detail
 * (e.g. "integer out of range", "null value in column X violates not-null").
 *
 * @since 1.0.0
 */
export const formatErrorCause = (error: unknown): string => {
  const parts: Array<string> = [];
  const seen = new Set<unknown>();
  let current: unknown = error;

  while (current !== null && current !== undefined && !seen.has(current)) {
    seen.add(current);

    if (typeof current !== "object") {
      parts.push(String(current));
      break;
    }

    const err = current as { _tag?: unknown; name?: unknown; message?: unknown; cause?: unknown };
    const tag = typeof err._tag === "string"
      ? err._tag
      : (typeof err.name === "string" ? err.name : undefined);
    const msg = typeof err.message === "string" && err.message.length > 0 ? err.message : undefined;

    if (tag && msg) {
      parts.push(`${tag}: ${msg}`);
    } else if (tag) {
      parts.push(tag);
    } else if (msg) {
      parts.push(msg);
    }

    current = err.cause;
  }

  return parts.length > 0 ? parts.join(" → ") : String(error);
};

export class RecordNotFoundError extends Schema.TaggedError<RecordNotFoundError>()(
  "RecordNotFoundError",
  {
    table: Schema.String,
    where: Schema.Record({ key: Schema.String, value: Schema.Unknown }),
    message: Schema.optional(Schema.String),
  },
) {}

export class UniqueConstraintError extends Schema.TaggedError<UniqueConstraintError>()(
  "UniqueConstraintError",
  {
    table: Schema.String,
    constraint: Schema.String,
    message: Schema.optional(Schema.String),
  },
) {}

/**
 * Walk an error's `cause` chain looking for a Postgres unique-violation
 * (SQLSTATE `23505`) and, if found, return the offending `constraint` + `table`.
 * Returns `null` for any other error.
 *
 * The node-postgres error that still carries the structured `code`/`constraint`
 * fields sits a few layers down the chain (SqlError → DrizzleQueryError → pg
 * error), so we follow `cause` until we reach it. This lets an accessor
 * translate a raw constraint violation into a typed {@link UniqueConstraintError}
 * inside its `catch`, before the cause chain is flattened to a string by
 * {@link formatErrorCause}.
 *
 * @since 1.0.0
 */
export const detectUniqueViolation = (
  error: unknown,
): { constraint: string; table: string } | null => {
  const seen = new Set<unknown>();
  let current: unknown = error;

  while (current !== null && current !== undefined && !seen.has(current)) {
    seen.add(current);

    if (typeof current !== "object") break;

    const err = current as { code?: unknown; constraint?: unknown; table?: unknown; cause?: unknown };
    if (String(err.code) === "23505") {
      return {
        constraint: typeof err.constraint === "string" ? err.constraint : "",
        table: typeof err.table === "string" ? err.table : "",
      };
    }

    current = err.cause;
  }

  return null;
};

/**
 * Translate a write-path failure into the typed error a customer/person
 * mutation surfaces: a Postgres unique violation (23505) becomes a
 * {@link UniqueConstraintError} carrying the offending table/constraint,
 * everything else a generic {@link DatabaseError}. Centralised here (and
 * unit-tested directly) so the accessor's `catch` stays branch-free — the
 * unique arm is otherwise only reachable under a concurrent-insert race a
 * single integration connection cannot reproduce.
 *
 * @since 1.0.0
 */
export const toWriteError = (error: unknown): DatabaseError | UniqueConstraintError => {
  const unique = detectUniqueViolation(error);
  return unique !== null
    ? new UniqueConstraintError({
      table: unique.table,
      constraint: unique.constraint,
      message: formatErrorCause(error),
    })
    : new DatabaseError({ message: formatErrorCause(error) });
};

/**
 * Raised when `allocateNext` is called but no bots are available in the pool.
 *
 * @since 1.0.0
 */
export class DiscordBotPoolExhaustedError extends Schema.TaggedError<DiscordBotPoolExhaustedError>()(
  "DiscordBotPoolExhaustedError",
  {
    message: Schema.optional(Schema.String),
  },
) {}
